<template>
  <div>
    <h2>Composition of an Alloy: Alloy Calculations</h2>
    <v-form>
      <!-- Data Table -->
      <h3 class="mb-4 mt-6">Data Table</h3>
      <v-textarea
        v-model="inputs.data_table_caption"
        label="Table Caption"
        outlined
        rows="1"
        class="mb-n3"
        placeholder="Write a caption for the data table here."
      />
      <v-simple-table>
        <thead>
          <tr>
            <td>&nbsp;</td>
            <td style="text-align: center; font-weight: bold">Units</td>
            <td style="text-align: center; font-weight: bold">Trial 1</td>
            <td style="text-align: center; font-weight: bold">Trial 2</td>
          </tr>
        </thead>
        <tr>
          <td style="font-weight: bold">Unknown Number</td>
          <td>&nbsp;</td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <v-text-field v-model="inputs[`trial${i}_identity`]" outlined hide-details />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Mass</td>
          <td>
            <v-select
              v-model="inputs['mass_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input v-model="inputs[`trial${i}_mass`]" class="centered-input" />
          </td>
        </tr>
        <tr>
          <td><stemble-latex content="$p_i$" /></td>
          <td>
            <v-select
              v-model="inputs['initialPressure_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_initialPressure`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td><stemble-latex content="$p_f$" /></td>
          <td>
            <v-select
              v-model="inputs['finalPressure_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input v-model="inputs[`trial${i}_finalPressure`]" class="centered-input" />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Room Temperature</td>
          <td>
            <v-select
              v-model="inputs['roomTemperature_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_roomTemperature`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Mass of Empty Flask</td>
          <td>
            <v-select
              v-model="inputs['massEmptyFlask_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_massEmptyFlask`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Mass of Filled Flask</td>
          <td>
            <v-select
              v-model="inputs['massFilledFlask_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_massFilledFlask`]"
              class="centered-input"
            />
          </td>
        </tr>
      </v-simple-table>

      <!-- Unknown Metal Calculations Table -->
      <h3 class="mb-4 mt-8">Calculations: Unknown Alloy</h3>
      <v-textarea
        v-model="inputs.calculations_table_caption"
        label="Table Caption"
        outlined
        rows="1"
        class="mb-n3"
        placeholder="Write a caption for the alloy table here."
      />

      <v-simple-table>
        <thead>
          <tr>
            <td>&nbsp;</td>
            <td style="text-align: center; font-weight: bold">Units</td>
            <td style="font-weight: bold; text-align: center">Trial 1</td>
            <td style="font-weight: bold; text-align: center">Trial 2</td>
          </tr>
        </thead>
        <tr>
          <td style="font-weight: bold">Mass of Alloy</td>
          <td>
            <v-select
              v-model="inputs['massOfAlloy_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input v-model="inputs[`trial${i}_massOfAlloy`]" class="centered-input" />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Volume of Flask</td>
          <td>
            <v-select
              v-model="inputs['volumeOfFlask_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input v-model="inputs[`trial${i}_volumeOfFlask`]" class="centered-input" />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Volume of Flask Available</td>
          <td>
            <v-select
              v-model="inputs['volumeAvailable_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_volumeAvailable`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td><stemble-latex content="$\Delta p$" /></td>
          <td>
            <v-select
              v-model="inputs['changeInPressure_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_changeInPressure`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Amount of Hydrogen (Experimental)</td>
          <td>
            <v-select
              v-model="inputs['molesOfHydrogenGasActual_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_molesOfHydrogenGasActual`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Mass of Zinc</td>
          <td>
            <v-select
              v-model="inputs['massOfZinc_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input v-model="inputs[`trial${i}_massOfZinc`]" class="centered-input" />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Mass of Aluminum</td>
          <td>
            <v-select
              v-model="inputs['massOfAluminum_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_massOfAluminum`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Percent Zinc</td>
          <td>
            <v-select
              v-model="inputs['percentMassOfZinc_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_percentMassOfZinc`]"
              class="centered-input"
            />
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold">Percent Aluminum</td>
          <td>
            <v-select
              v-model="inputs['percentMassOfAluminum_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>
          <td v-for="i in 2" :key="`unknown-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_percentMassOfAluminum`]"
              class="centered-input"
            />
          </td>
        </tr>
      </v-simple-table>

      <p class="mt-5">
        In the space provided below, please type out your sample calculations for this part of the
        experiment. Be sure to show all your work.
      </p>

      <v-textarea v-model="inputs.sampleCalc" outlined rows="6" auto-grow />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import StembleLatex from '@/tasks/components/StembleLatex.vue';

export default {
  name: 'OttawaLab1ReportAlloyData',
  components: {StembleLatex, CalculationInput},
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        data_table_caption: null,
        calculations_table_caption: null,

        mass_units: null,
        initialPressure_units: null,
        finalPressure_units: null,
        roomTemperature_units: null,
        massEmptyFlask_units: null,
        massFilledFlask_units: null,

        massOfAlloy_units: null,
        volumeOfFlask_units: null,
        volumeAvailable_units: null,
        changeInPressure_units: null,
        molesOfHydrogenGasActual_units: null,
        massOfZinc_units: null,
        massOfAluminum_units: null,
        percentMassOfZinc_units: null,
        percentMassOfAluminum_units: null,

        trial1_identity: null,
        trial1_mass: null,
        trial1_initialPressure: null,
        trial1_finalPressure: null,
        trial1_roomTemperature: null,
        trial1_massEmptyFlask: null,
        trial1_massFilledFlask: null,
        trial2_identity: null,
        trial2_mass: null,
        trial2_initialPressure: null,
        trial2_finalPressure: null,
        trial2_roomTemperature: null,
        trial2_massEmptyFlask: null,
        trial2_massFilledFlask: null,

        trial1_massOfAlloy: null,
        trial1_volumeOfFlask: null,
        trial1_volumeAvailable: null,
        trial1_changeInPressure: null,
        trial1_molesOfHydrogenGasActual: null,
        trial1_massOfZinc: null,
        trial1_massOfAluminum: null,
        trial1_percentMassOfZinc: null,
        trial1_percentMassOfAluminum: null,
        trial2_massOfAlloy: null,
        trial2_volumeOfFlask: null,
        trial2_volumeAvailable: null,
        trial2_changeInPressure: null,
        trial2_molesOfHydrogenGasActual: null,
        trial2_massOfZinc: null,
        trial2_massOfAluminum: null,
        trial2_percentMassOfZinc: null,
        trial2_percentMassOfAluminum: null,

        sampleCalc: null,
      },
      units: [
        {key: 'g', label: '$\\text{g}$'},
        {key: 'kPa', label: '$\\text{kPa}$'},
        {key: 'mL', label: '$\\text{mL}$'},
        {key: 'gPerMol', label: '$\\text{g} \\cdot \\text{mol}^{-1}$'},
        {key: 'degreesCelsius', label: '$^\\omicron\\text{C}$'},
        {key: 'gramsPerMillilitre', label: '$\\text{g} \\cdot \\text{mL}^{-1}$'},
        {
          key: 'joulesPerKelvinPerMole',
          label: '$\\text{J} \\cdot \\text{K}^{-1} \\cdot \\text{mol}^{-1}$',
        },
        {
          key: 'moles',
          label: '$\\text{mol}$',
        },
        {
          key: 'percent',
          label: '$\\text{\\%}$',
        },
      ],
    };
  },
};
</script>
